import React, { useState } from 'react';
import { Bell, BellOff, BellRing, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePushNotifications } from '../hooks/usePushNotifications';

/**
 * PushNotificationsBanner
 * Shown on the school admin dashboard when notifications aren't yet enabled.
 * Also used inside Settings for the full toggle panel.
 */
export function PushNotificationsBanner() {
  const { permission, subscribed, loading, subscribe } = usePushNotifications();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('push_banner_dismissed') === '1'
  );

  // Don't show if: already subscribed, denied, not supported, dismissed
  if (subscribed || permission === 'denied' || dismissed) return null;
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;

  const handleEnable = async () => {
    try {
      await subscribe();
      toast.success('Push notifications enabled!');
    } catch (e) {
      if (e.message === 'Permission denied') {
        toast.error('Notifications blocked. Please allow them in browser settings.');
      } else {
        toast.error('Could not enable notifications. Try again later.');
      }
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('push_banner_dismissed', '1');
  };

  return (
    <div
      className="flex items-center gap-3 bg-[#2563eb]/8 border border-[#2563eb]/20 rounded-xl px-4 py-3 mb-4"
      data-testid="push-notifications-banner"
    >
      <div className="w-8 h-8 rounded-lg bg-[#2563eb]/15 flex items-center justify-center flex-shrink-0">
        <Bell className="w-4 h-4 text-[#2563eb]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">Enable Push Notifications</p>
        <p className="text-xs text-slate-500 mt-0.5">
          Get alerts for new admissions, fee payments, and low attendance
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleEnable}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white text-xs font-semibold rounded-lg transition-colors"
          data-testid="enable-push-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
          Enable
        </button>
        <button
          onClick={handleDismiss}
          className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1.5 transition-colors"
          data-testid="dismiss-push-btn"
        >
          Later
        </button>
      </div>
    </div>
  );
}

/**
 * PushNotificationsPanel
 * Full settings panel with toggle + test button — placed in Settings page.
 */
export function PushNotificationsPanel() {
  const { permission, subscribed, loading, subscribe, unsubscribe, sendTest } = usePushNotifications();
  const supported = 'serviceWorker' in navigator && 'PushManager' in window;

  const handleToggle = async () => {
    try {
      if (subscribed) {
        await unsubscribe();
        toast.success('Push notifications disabled');
      } else {
        await subscribe();
        toast.success('Push notifications enabled!');
      }
    } catch (e) {
      toast.error(e.message || 'Could not update notification settings');
    }
  };

  const handleTest = async () => {
    try {
      const res = await sendTest();
      if (res.sent > 0) {
        toast.success('Test notification sent! Check your device.');
      } else {
        toast.error('No subscription found. Enable notifications first.');
      }
    } catch {
      toast.error('Failed to send test notification');
    }
  };

  if (!supported) {
    return (
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="push-not-supported">
        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-800">Not Supported</p>
          <p className="text-xs text-amber-600 mt-0.5">Your browser doesn't support push notifications. Try Chrome or Edge.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="push-notifications-panel">
      <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-xl">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${subscribed ? 'bg-green-100' : 'bg-slate-100'}`}>
            {subscribed ? <BellRing className="w-4.5 h-4.5 text-green-600" /> : <BellOff className="w-4.5 h-4.5 text-slate-400" />}
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Push Notifications</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {permission === 'denied'
                ? 'Blocked in browser — update site settings to enable'
                : subscribed
                ? 'Active on this device'
                : 'New admissions, payments, low attendance'}
            </p>
          </div>
        </div>
        <button
          onClick={handleToggle}
          disabled={loading || permission === 'denied'}
          className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50
            ${subscribed ? 'bg-[#2563eb]' : 'bg-slate-200'}`}
          data-testid="push-toggle-btn"
          aria-label="Toggle push notifications"
        >
          {loading
            ? <Loader2 className="w-3 h-3 absolute top-1.5 left-1.5 text-white animate-spin" />
            : <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${subscribed ? 'translate-x-5' : 'translate-x-0.5'}`} />
          }
        </button>
      </div>

      {subscribed && (
        <button
          onClick={handleTest}
          className="w-full flex items-center justify-center gap-2 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition-colors"
          data-testid="push-test-btn"
        >
          <CheckCircle2 className="w-4 h-4 text-green-500" />
          Send Test Notification
        </button>
      )}

      <div className="space-y-1.5 px-1">
        {[
          { icon: '🎓', label: 'New student admitted to school' },
          { icon: '💳', label: 'Full fee payment received' },
          { icon: '⚠️', label: 'Class attendance drops below 75%' },
        ].map(({ icon, label }) => (
          <div key={label} className="flex items-center gap-2 text-xs text-slate-500">
            <span>{icon}</span>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
